import { useState, useEffect } from 'react';
import { format, parseISO, isPast } from 'date-fns';
import { ko } from 'date-fns/locale';
// @mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Button,
  Typography,
  List,
  ListItem,
  Box,
} from '@mui/material';
// hooks
import useGoals from '../../hooks/useGoals';
import useResponsive from '../../hooks/useResponsive';
// components
import Iconify from '../../components/Iconify';

export default function GoalListDialog({ isOpen, closeDialog, companyId, crewId }) {
  const smUp = useResponsive('up', 'sm');
  const { goals } = useGoals(companyId, crewId);
  const [showCompleted, setShowCompleted] = useState(false);

  // 다이얼로그가 닫힐 때 완료 목록도 접기
  useEffect(() => {
    if (!isOpen) {
      setShowCompleted(false);
    }
  }, [isOpen]);

  const incompleteGoals = goals.filter(goal => !goal.is_completed);
  const completedGoals = goals.filter(goal => goal.is_completed);

  return (
    <>
      <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="sm" fullScreen={!smUp}>
        <DialogTitle variant="h6" sx={{ pb: 1 }}>
          회사의 요청사항 자세히 보기
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {goals.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                등록된 요청사항이 없습니다
              </Typography>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {incompleteGoals.length > 0 && (
                <>
                  <ListItem sx={{ bgcolor: 'background.neutral', py: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                      진행 중 ({incompleteGoals.length})
                    </Typography>
                  </ListItem>
                  {incompleteGoals.map((goal) => {
                    const isOverdue = goal.due_date && isPast(parseISO(goal.due_date));
                    return (
                      <ListItem key={goal.id} divider sx={{ display: 'block', p: 3 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <Box sx={{ flex: 1 }}>
                              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                <Typography variant="subtitle1">
                                  {goal.content}
                                </Typography>
                              </Stack>
                              <Typography variant="body2" sx={{ color: isOverdue ? 'error.main' : 'text.primary', mt: 0.5, display: 'block' }}>
                                {goal.due_date
                                  ? `목표일자: ${format(parseISO(goal.due_date), 'yyyy년 MM월 dd일', { locale: ko })}`
                                  : '기한 없음'}
                              </Typography>
                            </Box>
                          </Stack>
                        </Stack>
                      </ListItem>
                    );
                  })}
                </>
              )}
              
              {completedGoals.length > 0 && (
                <>
                  <ListItem
                    sx={{ 
                      bgcolor: 'background.neutral', 
                      py: 1, 
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      }
                    }}
                    onClick={() => setShowCompleted(!showCompleted)}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                        완료됨 ({completedGoals.length})
                      </Typography>
                      <Iconify 
                        icon={showCompleted ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} 
                        sx={{ color: 'text.secondary' }}
                      />
                    </Stack>
                  </ListItem>
                  {showCompleted && completedGoals.map((goal) => (
                    <ListItem key={goal.id} divider sx={{ display: 'block', p: 3 }}>
                      <Stack spacing={1}>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ color: 'text.disabled' }}
                          >
                            {goal.content}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                            {goal.completed_at
                              ? `완료일: ${format(parseISO(goal.completed_at), 'yyyy년 MM월 dd일', { locale: ko })}`
                              : ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </ListItem>
                  ))}
                </>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeDialog} variant="contained" size="large" sx={{ px: 4 }}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}